/**
 * Map view tuning (M3 plan R14): `src/maps/view/view.json`, Zod-validated here. The model's data and colours stay in
 * `src/data/reach-maps.json` (R9); this file holds only how the view draws and how its input feels.
 */
import { z } from 'zod'
import viewJson from './view.json'

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'expected #RRGGBB')
const positive = z.number().positive()
const axis = z.number().int().min(0).max(15)
const button = z.number().int().min(0).max(31)

export const mapsViewConfigSchema = z.object({
  comment: z.string().optional(),
  backdrop: z.object({ color: hex, opacity: z.number().min(0).max(1) }),
  cursor: z.object({ speedPxS: positive, stickDeadzone: z.number().min(0).max(0.9) }),
  pick: z.object({ radiusPx: positive, dragSlopPx: z.number().min(0) }),
  zoom: z.object({ wheelNotchPx: positive, triggerRate: positive, triggerThreshold: z.number().min(0).max(0.9) }),
  pad: z.object({
    cursorAxes: z.tuple([axis, axis]),
    orbitAxes: z.tuple([axis, axis]),
    zoomOutTrigger: button,
    zoomInTrigger: button,
  }),
  galaxy: z.object({
    dimFactor: z.number().min(0).max(1),
    dashSize: positive,
    gapSize: positive,
    selectRingScale: positive,
    currentRingScale: positive,
    currentColor: hex,
    previewColor: hex,
    routeWidth: positive,
    /** M4 plan I7: tube radius (map units) per unit of an overlay's relay width. */
    flowWidth: positive,
  }),
  system: z.object({
    colors: z.object({
      star: hex,
      station: hex,
      rocky: hex,
      icy: hex,
      lava: hex,
      gas: hex,
      field: hex,
      poi: hex,
      poiDone: hex,
      laneUp: hex,
      laneDown: hex,
      orbit: hex,
      player: hex,
      selection: hex,
    }),
    fieldOpacity: z.number().min(0).max(1),
    orbitSegments: z.number().int().min(8).max(512),
    playerSize: positive,
    selectRingScale: positive,
  }),
  labels: z.object({ charPx: positive, heightPx: positive, padPx: z.number().min(0), systemMax: z.number().int().min(0), offscreenPx: z.number().min(0) }),
  toastS: positive,
  refreshS: z.number().min(0),
  regionNames: z.object({ gate: z.string().min(1), mid: z.string().min(1), deep: z.string().min(1), rim: z.string().min(1) }),
})

export type MapsViewConfig = z.infer<typeof mapsViewConfigSchema>

/** Parses and validates view.json (throws a ZodError naming the bad field). */
export function loadMapsViewConfig(raw: unknown = viewJson): MapsViewConfig {
  return mapsViewConfigSchema.parse(raw)
}
